import { useMemo, useState } from "react";
import {
  Label,
  Input,
  Button,
  SheetFooter,
  SheetClose,
  DatePicker,
  useDateFnsFormattersWithLocale,
} from "@tellescope/ui";
import { StateSelectField, useForm } from "@tellescope/forms";
import { useTranslation } from "react-i18next";
import {
  useAuth,
  useTellescopeMutation,
  useTellescopeQuery,
  type TellescopeModelFromName,
} from "@tellescope/tanstack-query";
import { createKeyMap } from "@tellescope/utils";
import { toast } from "sonner";

type EnduserRecord = TellescopeModelFromName<"endusers">;

interface ContactFormProps {
  mode?: "create" | "update";
  contactId?: string;
  onSuccess?: (contact: EnduserRecord) => void;
  onCancel?: () => void;
  className?: string;
}

export function ContactForm({
  mode = "create",
  contactId,
  onSuccess,
  onCancel,
  className: _className,
}: ContactFormProps) {
  const { t } = useTranslation("endusers");
  const { format } = useDateFnsFormattersWithLocale();
  const { selectedOrganization } = useAuth();

  const originalEnduserQuery = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: contactId ?? "",
    enabled: !!contactId,
  });

  const contact = originalEnduserQuery.data;

  const createEnduserMutation = useTellescopeMutation({
    model: "endusers",
    method: "createOne",
    onSuccess,
    onError: () => {
      toast.error(t("Failed to create contact. Please try again."));
    },
  });

  const updateEnduserMutation = useTellescopeMutation({
    model: "endusers",
    method: "updateOne",
    onSuccess,
    onError: () => {
      toast.error(t("Failed to update contact. Please try again."));
    },
  });

  const fieldConfigs = useMemo(
    () => createKeyMap(selectedOrganization?.settings?.endusers?.builtinFields ?? [], "field"),
    [selectedOrganization?.settings?.endusers?.builtinFields]
  );

  const form = useForm({
    fields: {
      fname: {
        type: "text",
        required: fieldConfigs["fname"]?.required,
        initialValue: contact?.fname || "",
      },
      lname: {
        type: "text",
        required: fieldConfigs["lname"]?.required,
        initialValue: contact?.lname || "",
      },
      email: {
        type: "email",
        required: fieldConfigs["email"]?.required,
        initialValue: contact?.email || "",
      },
      phone: {
        type: "text",
        required: fieldConfigs["phone"]?.required,
        initialValue: contact?.phone || "",
      },
      dateOfBirth: {
        type: "date",
        required: fieldConfigs["dateOfBirth"]?.required,
        initialValue: contact?.dateOfBirth ? new Date(contact.dateOfBirth) : undefined,
      },
      primaryInsurance: {
        type: "text",
        required: fieldConfigs["primaryInsurance"]?.required,
        initialValue: contact?.insurance?.planName || "",
      },
      secondaryInsurance: {
        type: "text",
        required: fieldConfigs["secondaryInsurance"]?.required,
        initialValue: contact?.insuranceSecondary?.planName || "",
      },
      addressLineOne: {
        type: "text",
        required: fieldConfigs["addressLineOne"]?.required,
        initialValue: contact?.addressLineOne || "",
      },
      addressLineTwo: {
        type: "text",
        required: fieldConfigs["addressLineTwo"]?.required,
        initialValue: contact?.addressLineTwo || "",
      },
      city: {
        type: "text",
        required: fieldConfigs["city"]?.required,
        initialValue: contact?.city || "",
      },
      state: {
        type: "select",
        required: fieldConfigs["state"]?.required,
        initialValue: contact?.state || "",
      },
      zipCode: {
        type: "text",
        required: fieldConfigs["zipCode"]?.required,
        initialValue: contact?.zipPlusFour ? `${contact.zipCode}-${contact.zipPlusFour}` : contact?.zipCode || "",
      },
    },
    validateOn: "blur",
    validators: {
      email: (value) => {
        if (value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
          return t("Invalid email address");
        }
        return undefined;
      },
      phone: (value) => {
        if (value && !/^\+?[1-9]\d{1,14}$/.test(value)) {
          return t("Invalid phone number");
        }
        return undefined;
      },
      zipCode: (val) => {
        if (!val) return undefined;
        return /^\d{5}(-\d{4})?$/.test(val) ? undefined : t("Please enter a valid zip code");
      },
    },
    onSubmit: async (values) => {
      const [zipCode, zipCodePlusFour] = (values.zipCode || "").split("-");
      const payload = {
        fname: values.fname,
        lname: values.lname,
        email: values.email,
        phone: values.phone,
        dateOfBirth: values.dateOfBirth ? format(values.dateOfBirth, "MM-dd-yyyy") : "",
        insurance: {
          ...(contact?.insurance ?? {}),
          planName: values.primaryInsurance,
        },
        insuranceSecondary: {
          ...(contact?.insuranceSecondary ?? {}),
          planName: values.secondaryInsurance,
        },
        addressLineOne: values.addressLineOne,
        addressLineTwo: values.addressLineTwo,
        city: values.city,
        state: values.state,
        zipCode: zipCode || "",
        zipPlusFour: zipCodePlusFour || "",
      };

      switch (mode) {
        case "create":
          await createEnduserMutation.mutateAsync(payload);
          return;
        case "update":
          if (!contactId) return;
          await updateEnduserMutation.mutateAsync({
            id: contactId,
            ...payload,
          });
          return;
      }
    },
  });

  const isPending = createEnduserMutation.isPending || updateEnduserMutation.isPending;

  return (
    <>
      <form className="flex flex-1 flex-col overflow-hidden" {...form.getFormProps()}>
        <div className="grid flex-1 auto-rows-min gap-4 px-4 overflow-y-auto">
          {/* First Name */}
          {!(fieldConfigs["fname"]?.hidden ?? false) && (
            <div className="grid gap-1.5">
              <Label htmlFor="fname" required={fieldConfigs["fname"]?.required}>
                {t("First Name")}
              </Label>
              <Input id="fname" {...form.getFieldProps("fname")} placeholder={t("Enter first name...")} />
            </div>
          )}

          {/* Last Name */}
          {!(fieldConfigs["lname"]?.hidden ?? false) && (
            <div className="grid gap-1.5">
              <Label htmlFor="lname" required={fieldConfigs["lname"]?.required}>
                {t("Last Name")}
              </Label>
              <Input id="lname" {...form.getFieldProps("lname")} placeholder={t("Enter last name...")} />
            </div>
          )}

          {/* Email */}
          {!(fieldConfigs["email"]?.hidden ?? false) && (
            <div className="grid gap-1.5">
              <Label htmlFor="email" required={fieldConfigs["email"]?.required}>
                {t("Email")}
              </Label>
              <Input id="email" {...form.getFieldProps("email")} placeholder={t("Enter email...")} />
            </div>
          )}

          {/* Phone */}
          {!(fieldConfigs["phone"]?.hidden ?? false) && (
            <div className="grid gap-1.5">
              <Label htmlFor="phone" required={fieldConfigs["phone"]?.required}>
                {t("Phone")}
              </Label>
              <Input id="phone" {...form.getFieldProps("phone")} placeholder={t("Enter phone number...")} />
            </div>
          )}

          {/* Date of Birth */}
          {!(fieldConfigs["dateOfBirth"]?.hidden ?? false) && (
            <div className="grid gap-1.5">
              <Label htmlFor="dob" required={fieldConfigs["dateOfBirth"]?.required}>
                {t("Date of Birth")}
              </Label>
              <DatePicker
                id="dob"
                {...form.getCustomFieldProps("dateOfBirth")}
                placeholder={t("Select date...")}
                className="space-y-0"
                label=""
              />
            </div>
          )}

          {/* Primary Insurance */}
          {!(fieldConfigs["primaryInsurance"]?.hidden ?? false) && (
            <div className="grid gap-1.5">
              <Label htmlFor="primaryInsurance" required={fieldConfigs["primaryInsurance"]?.required}>
                {t("Primary Insurance")}
              </Label>
              <Input
                id="primaryInsurance"
                {...form.getFieldProps("primaryInsurance")}
                placeholder={t("Enter primary insurance...")}
              />
            </div>
          )}

          {/* Secondary Insurance */}
          {!(fieldConfigs["secondaryInsurance"]?.hidden ?? false) && (
            <div className="grid gap-1.5">
              <Label htmlFor="secondaryInsurance" required={fieldConfigs["secondaryInsurance"]?.required}>
                {t("Secondary Insurance")}
              </Label>
              <Input
                id="secondaryInsurance"
                {...form.getFieldProps("secondaryInsurance")}
                placeholder={t("Enter secondary insurance...")}
              />
            </div>
          )}

          {/* Address Line 1 */}
          {!(fieldConfigs["addressLineOne"]?.hidden ?? false) && (
            <div className="grid gap-1.5">
              <Label htmlFor="addressLineOne" required={fieldConfigs["addressLineOne"]?.required}>
                {t("Address")}
              </Label>
              <Input
                id="addressLineOne"
                {...form.getFieldProps("addressLineOne")}
                placeholder={t("Enter address...")}
              />
            </div>
          )}

          {/* Address Line 2 */}
          {!(fieldConfigs["addressLineTwo"]?.hidden ?? false) && (
            <div className="grid gap-1.5">
              <Label htmlFor="addressLineTwo" required={fieldConfigs["addressLineTwo"]?.required}>
                {t("Address Line 2")}
              </Label>
              <Input
                id="addressLineTwo"
                {...form.getFieldProps("addressLineTwo")}
                placeholder={t("Enter address line 2...")}
              />
            </div>
          )}

          {/* City */}
          {!(fieldConfigs["city"]?.hidden ?? false) && (
            <div className="grid gap-1.5">
              <Label htmlFor="city" required={fieldConfigs["city"]?.required}>
                {t("City")}
              </Label>
              <Input id="city" {...form.getFieldProps("city")} placeholder={t("Enter city...")} />
            </div>
          )}

          {/* State */}
          {!(fieldConfigs["state"]?.hidden ?? false) && (
            <div className="grid gap-1.5">
              <Label htmlFor="state" required={fieldConfigs["state"]?.required}>
                {t("State")}
              </Label>
              <StateSelectField {...form.getCustomFieldProps("state")} className="flex-1" />
            </div>
          )}

          {/* ZIP Code */}
          {!(fieldConfigs["zipCode"]?.hidden ?? false) && (
            <div className="grid gap-1.5">
              <Label htmlFor="zipCode" required={fieldConfigs["zipCode"]?.required}>
                {t("ZIP Code")}
              </Label>
              <Input id="zipCode" {...form.getFieldProps("zipCode")} placeholder={t("Enter ZIP code...")} />
            </div>
          )}
        </div>

        <SheetFooter>
          <SheetClose asChild>
            <Button variant="outline" onClick={onCancel}>
              {t("Cancel")}
            </Button>
          </SheetClose>
          <Button type="submit" disabled={!form.isValid || isPending}>
            {mode === "create" ? t("Create Contact") : t("Update Contact")}
          </Button>
        </SheetFooter>
      </form>
    </>
  );
}

// Example usage component for Storybook
export function ContactFormExample() {
  const [showForm, setShowForm] = useState(false);
  const [mode, setMode] = useState<"create" | "update">("create");

  const handleSuccess = (contact: EnduserRecord) => {
    alert(`Contact ${mode}d: ${contact.fname} ${contact.lname}`);
    setShowForm(false);
  };

  const handleCancel = () => {
    setShowForm(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <button
          onClick={() => {
            setMode("create");
            setShowForm(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Create Contact
        </button>
      </div>

      {showForm && (
        <div className="border rounded-lg p-6">
          <ContactForm mode={mode} onSuccess={handleSuccess} onCancel={handleCancel} />
        </div>
      )}
    </div>
  );
}
